import { useEffect, useState } from 'react';

import { PC } from '@/_pages/types';
import { clientConfig } from '@/config/clientConfig';
import { useMnemonicPhraseStore } from '@/entities/mnemonic/store/mnemonic-phrase.store';
import { useNotices } from '@/features/notices/useNotices';
import { EnterSeedDialog } from '@/shared/components/seed/EnterSeedDialog';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { PageDialog } from '@/shared/layouts/PageDialog';
import { makeKeyPair } from '@/shared/lib/secure-json/helpers';
import { useFileLoader } from '@/shared/lib/sj-orm/synchronizer/hooks/useFileLoader';
import { useAuthStore } from '@/shared/store/auth.store';
// import { useCollectionsStatusStore } from '@/shared/lib/sj-orm/stores/collectionsStatus.store';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';

export const PageRootExportedMode: PC = () => {
  const router = useAppRouter();
  const dbLoader = useFileLoader();
  const authStore = useAuthStore();
  const keypairStore = useKeyPairStore();
  const { inputPhrase: mnemonicPhrase } = useMnemonicPhraseStore();
  // const colStatusStore = useCollectionsStatusStore();

  const notices = useNotices();

  const [error, setError] = useState('');
  const [pubKey, setPubKey] = useState('');
  const [ready, setReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const tryToLogin = async (mnemonic: string, originPublicKey: string) => {
    const keyPair = await makeKeyPair(mnemonic);

    if (keyPair === null || keyPair.publicKey !== originPublicKey) {
      return false;
    } else {
      keypairStore.setKeyPair(keyPair);
      return true;
    }
  };

  const onEnter = async (): Promise<void> => {
    setError('');
    setIsLoading(true);

    const mnemonic = mnemonicPhrase.trim();

    if (!mnemonic) {
      setError('Empty seed phrase');
      setIsLoading(false);
      return;
    }

    if (mnemonic.split(/\s+/).length < 12) {
      setError('Please, enter full mnemonic phrase');
      setIsLoading(false);
      return;
    }

    const res = await tryToLogin(mnemonic, pubKey);
    if (!res) {
      setError('Invalid mnemonic phrase');
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    await router.push({ pathname: '/dashboard' });
  };

  useEffect(() => {
    const tryAutoLogin = async (mnemonic: string, dbPublicKey: string) => {
      const res = await tryToLogin(mnemonic, dbPublicKey);
      if (!res) {
        notices.error('Invalid demo mnemonic phrase');
        return;
      }

      await router.push({ pathname: '/dashboard' });
    };

    const init = async () => {
      const data = await dbLoader.fetchDatabase();

      if (!data) {
        notices.error('[PageRootExportedMode] error fetch data');
        return;
      }

      const mainUserData = data.mainUserData;

      // Need force validation
      if (mainUserData?.publicKey) {
        setPubKey(mainUserData.publicKey);
      }
      authStore.setSchemaVersion(mainUserData.schemaVersion);

      if (clientConfig.demoHub.mnemonic && mainUserData?.publicKey) {
        tryAutoLogin(clientConfig.demoHub.mnemonic, mainUserData?.publicKey);
      } else {
        setReady(true);
      }
    };

    init();
  }, []);

  return (
    <PageDialog>
      {ready && <EnterSeedDialog isLoading={isLoading} error={error} onEnter={onEnter} />}
    </PageDialog>
  );
};
